import React, { useMemo, useState } from 'react'

import {
  Link as RouterLink,
  useLocation,
  matchPath as routerMatchPath,
} from 'react-router-dom'

import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { SearchModal } from '@tribeplatform/react-components/Search'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { BottomBar } from '@tribeplatform/react-ui-kit/BottomBar'

import {
  defaultBottomBarControlItemsFactory,
  NavbarControlItem,
  NavbarControlAction,
} from './constants'
import { NavbarControlCreateItem } from './controls/NavbarControlCreateItem'
import { MessagingIndicatorBadge } from './MessagingIndicatorBadge'
import { NotificationIndicatorBadge } from './NotificationIndicatorBadge'
import { getIcon, getNavbarControlPresentation } from './utils'

export const NavigationBottom = ({
  controlItems,
}: {
  controlItems?: NavbarControlItem[]
}) => {
  const {
    data: { network },
  } = useAuthToken()
  const { pathname } = useLocation()
  const { generatePath, hasViewPermission, matchPath } = useSpaceUtil()
  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const defaultBottomControlItems = useStaticIntl(
    defaultBottomBarControlItemsFactory,
  )
  const navbarControlItems = useMemo(() => {
    if (!controlItems) {
      return defaultBottomControlItems
    }
    return controlItems
  }, [controlItems, defaultBottomControlItems])

  const { privateMessagingEnabled } = network?.settings?.privateMessaging || {}

  const getPresentation = ({ item, props = {} }) =>
    getNavbarControlPresentation({
      item,
      props,
    })

  const HomeIcon = getIcon('home', !!matchPath(SystemSpaceSlug.HOME, pathname))
  const SearchIcon = getIcon(
    'search',
    searchOpen || !!matchPath(SystemSpaceSlug.SEARCH, pathname),
  )
  const NotificationsIcon = getIcon(
    'bell',
    !!matchPath(SystemSpaceSlug.NOTIFICATIONS, pathname),
  )
  const MessagingIcon = getIcon(
    'chat',
    !!routerMatchPath(pathname, {
      path: `/messaging`,
    }),
  )

  return (
    <>
      <BottomBar>
        <BottomBar.Link
          icon={<HomeIcon />}
          as={RouterLink}
          active={!!matchPath(SystemSpaceSlug.HOME, pathname)}
          to={generatePath(SystemSpaceSlug.HOME)}
          onClick={() => {
            if (matchPath(SystemSpaceSlug.HOME, pathname)) {
              window.scrollTo(0, 0)
            }
          }}
        />
        <BottomBar.Link
          icon={<SearchIcon />}
          as="button"
          onClick={() => {
            setSearchOpen(true)
          }}
          active={searchOpen || !!matchPath(SystemSpaceSlug.SEARCH, pathname)}
        />
        {navbarControlItems.map(item => {
          switch (item.action) {
            case NavbarControlAction.CREATE_ITEM:
              return (
                <NavbarControlCreateItem
                  key={item.label}
                  item={item}
                  mobile
                  presentation={getPresentation({
                    item,
                    props: {
                      variant: 'primary',
                      rounded: true,
                      className: 'w-10 h-10 px-0 justify-center',
                      as: 'div',
                    },
                  })}
                />
              )
            case NavbarControlAction.SHOW_NOTIFICATION:
              if (!hasViewPermission(SystemSpaceSlug.NOTIFICATIONS)) {
                return null
              }

              return (
                <BottomBar.Link
                  key={item.label}
                  as={RouterLink}
                  to={generatePath(SystemSpaceSlug.NOTIFICATIONS)}
                  active={!!matchPath(SystemSpaceSlug.NOTIFICATIONS, pathname)}
                  icon={
                    <NotificationIndicatorBadge>
                      <NotificationsIcon className="h-6 w-6" />
                    </NotificationIndicatorBadge>
                  }
                />
              )
            case NavbarControlAction.SHOW_MESSAGING:
              if (!privateMessagingEnabled) {
                return null
              }
              return (
                <BottomBar.Link
                  key={item.label}
                  as={RouterLink}
                  to="/messaging"
                  icon={
                    <MessagingIndicatorBadge>
                      <MessagingIcon className="h-6 w-6" />
                    </MessagingIndicatorBadge>
                  }
                  active={
                    !!routerMatchPath(pathname, {
                      path: `/messaging`,
                    })
                  }
                />
              )
            default:
              return null
          }
        })}
      </BottomBar>

      {searchOpen && (
        <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      )}
    </>
  )
}
